import { useState } from "react";
import { MailCheck, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type ApiResponse = {
  error?: string;
  email?: string;
  expiresInSeconds?: number;
  resendAfterSeconds?: number;
  retryAfterSeconds?: number;
};

export function EmailChangeCard() {
  const { user, refetch } = useAuth();
  const { toast } = useToast();
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [code, setCode] = useState("");
  const [expiresInSeconds, setExpiresInSeconds] = useState(600);
  const [stage, setStage] = useState<"request" | "verify">("request");
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);

  if (user?.role !== "admin" && user?.role !== "superadmin") return null;

  const handleRequestCode = async () => {
    setRequesting(true);
    try {
      const response = await fetch(`${BASE}/api/auth/email-change/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ newEmail: newEmail.trim(), currentPassword }),
      });
      const data = await response.json().catch(() => ({})) as ApiResponse;
      if (!response.ok) {
        const wait = data.retryAfterSeconds
          ? ` Try again in ${data.retryAfterSeconds} seconds.`
          : "";
        toast({
          title: "Could not send verification code",
          description: `${data.error ?? "Please try again."}${wait}`,
          variant: "destructive",
        });
        return;
      }

      setNewEmail(data.email ?? newEmail.trim().toLowerCase());
      setExpiresInSeconds(data.expiresInSeconds ?? 600);
      setCode("");
      setCurrentPassword("");
      setStage("verify");
      toast({
        title: "Verification code ready",
        description: `Enter the code sent to ${data.email ?? newEmail}. Your emails will not change until you confirm it.`,
      });
    } catch {
      toast({
        title: "Could not send verification code",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setRequesting(false);
    }
  };

  const handleVerifyCode = async () => {
    setVerifying(true);
    try {
      const response = await fetch(`${BASE}/api/auth/email-change/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code }),
      });
      const data = await response.json().catch(() => ({})) as ApiResponse;
      if (!response.ok) {
        toast({
          title: "Email not changed",
          description: data.error ?? "The code could not be verified. Check it and try again.",
          variant: "destructive",
        });
        return;
      }

      try {
        await refetch();
      } catch {
        // The server confirmed the change; auth data can refresh on the next load.
      }
      toast({
        title: "Email updated",
        description: "Your sign-in and store contact email have both been changed.",
      });
      setNewEmail("");
      setCode("");
      setStage("request");
    } catch {
      toast({
        title: "Email not changed",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MailCheck className="h-4 w-4" /> Change email
        </CardTitle>
        <CardDescription>
          Update your sign-in email and your store contact email together. The new address must be verified first.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {stage === "request" ? (
          <>
            <div className="flex gap-3 rounded-lg border bg-muted/30 p-3 text-sm">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <p className="text-muted-foreground">
                We’ll send a one-time code to the new address. You’ll need your current password to request it.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="email-change-new">New email address</Label>
                <Input
                  id="email-change-new"
                  type="email"
                  autoComplete="email"
                  value={newEmail}
                  onChange={event => setNewEmail(event.target.value)}
                  placeholder="you@example.com"
                  maxLength={254}
                  disabled={requesting}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email-change-password">Current password</Label>
                <Input
                  id="email-change-password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={event => setCurrentPassword(event.target.value)}
                  disabled={requesting}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={handleRequestCode}
                disabled={requesting || !newEmail.trim() || !currentPassword}
              >
                {requesting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send verification code
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-lg border bg-muted/30 p-3 text-sm">
              <p className="font-medium">Code sent to {newEmail}</p>
              <p className="mt-1 text-muted-foreground">
                It expires in about {Math.max(1, Math.ceil(expiresInSeconds / 60))} minute(s). Neither email changes until verification succeeds.
              </p>
            </div>
            <div className="max-w-xs space-y-1.5">
              <Label htmlFor="email-change-code">Six-digit verification code</Label>
              <Input
                id="email-change-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={event => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                disabled={verifying}
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setStage("request");
                  setCode("");
                }}
                disabled={verifying}
              >
                Back
              </Button>
              <Button onClick={handleVerifyCode} disabled={verifying || code.length !== 6}>
                {verifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Verify and update email
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}